import React, { useState, useEffect, useMemo } from 'react';
import { X, FileDown, Activity, Users, Filter, Flame, Clock } from 'lucide-react';
import { dataRoomAPI } from '../../../services/api';

const ACTION_LABELS = {
  room_created: 'Room created',
  room_updated: 'Room updated',
  room_deleted: 'Room deleted',
  folder_created: 'Folder created',
  folder_updated: 'Folder updated',
  folder_deleted: 'Folder deleted',
  file_uploaded: 'File uploaded',
  file_renamed: 'File renamed',
  file_deleted: 'File deleted',
  file_download: 'File downloaded',
  file_view_start: 'File view started',
  file_view_end: 'File view ended',
  file_print_attempt: 'Print attempted',
  user_invited: 'User invited',
  user_revoked: 'User revoked',
  user_reinstated: 'User reinstated',
  user_deleted: 'User removed',
  user_updated: 'User updated',
  member_tagged: 'Member tagged',
  member_untagged: 'Member untagged',
  role_created: 'Role created',
  role_updated: 'Role updated',
  role_deleted: 'Role deleted',
  permission_changed: 'Permission changed',
  permission_deleted: 'Permission deleted',
  file_flagged_high_value: 'File flagged high-value',
};

const ACTION_COLORS = {
  file_view_start: 'bg-blue-50 text-blue-700',
  file_view_end: 'bg-indigo-50 text-indigo-700',
  file_download: 'bg-emerald-50 text-emerald-700',
  file_uploaded: 'bg-sky-50 text-sky-700',
  file_print_attempt: 'bg-red-50 text-red-700',
  permission_changed: 'bg-purple-50 text-purple-700',
  user_invited: 'bg-amber-50 text-amber-700',
  user_revoked: 'bg-red-50 text-red-700',
  file_flagged_high_value: 'bg-orange-50 text-orange-700',
};

function formatDuration(sec) {
  if (!sec) return '0s';
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  return `${(sec / 3600).toFixed(1)}h`;
}

export default function ActivityDashboardModal({ open, onClose, dataRoomId }) {
  const [tab, setTab] = useState('logs');
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ user_id: '', action: '', from_date: '', to_date: '' });

  useEffect(() => {
    if (!open) return;
    load();
  }, [open, tab]);

  const load = async () => {
    setLoading(true);
    try {
      if (tab === 'logs') {
        const res = await dataRoomAPI.listAuditLogs(dataRoomId, { ...filters, page_size: 200 });
        setLogs(res.data.data || []);
      } else {
        const res = await dataRoomAPI.activitySummary(dataRoomId);
        setSummary(res.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(dataRoomAPI.exportAuditLogsXlsx(dataRoomId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const a = document.createElement('a');
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.download = `audit-logs-${dataRoomId.slice(0, 8)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed');
    }
  };

  const uniqueUsers = useMemo(() => {
    const map = new Map();
    logs.forEach(l => {
      if (l.actor_id) map.set(l.actor_id, l.actor_email || l.actor_id);
    });
    return Array.from(map.entries());
  }, [logs]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" data-testid="activity-modal">
      <div className="bg-white rounded-lg w-full max-w-6xl h-[90vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <Activity size={18} className="text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Activity Dashboard</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              className="inline-flex items-center px-3 py-1.5 border rounded-lg text-xs hover:bg-gray-50"
              data-testid="export-audit-btn"
            >
              <FileDown size={12} className="mr-1" /> Export Excel
            </button>
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700"><X size={18} /></button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b px-5">
          <div className="flex gap-4">
            {[
              { id: 'logs', label: 'Activity Log', icon: Filter },
              { id: 'leaderboard', label: 'Leaderboard', icon: Users },
              { id: 'heatmap', label: 'Engagement Heatmap', icon: Flame },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`py-2.5 text-sm font-medium border-b-2 inline-flex items-center gap-1.5 ${tab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600'}`}
                data-testid={`tab-${t.id}`}
              >
                <t.icon size={13} /> {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-5">
          {loading ? (
            <div className="py-8 text-center text-gray-500 text-sm">Loading...</div>
          ) : tab === 'logs' ? (
            <>
              {/* Filters */}
              <div className="flex flex-wrap items-end gap-2 mb-4 pb-3 border-b">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">User</label>
                  <select
                    value={filters.user_id}
                    onChange={e => setFilters(f => ({ ...f, user_id: e.target.value }))}
                    className="border rounded px-2 py-1 text-xs"
                    data-testid="filter-user"
                  >
                    <option value="">All users</option>
                    {uniqueUsers.map(([id, email]) => <option key={id} value={id}>{email}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Action</label>
                  <select
                    value={filters.action}
                    onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}
                    className="border rounded px-2 py-1 text-xs"
                    data-testid="filter-action"
                  >
                    <option value="">All actions</option>
                    {Object.entries(ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">From</label>
                  <input
                    type="date"
                    value={filters.from_date}
                    onChange={e => setFilters(f => ({ ...f, from_date: e.target.value }))}
                    className="border rounded px-2 py-1 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">To</label>
                  <input
                    type="date"
                    value={filters.to_date}
                    onChange={e => setFilters(f => ({ ...f, to_date: e.target.value }))}
                    className="border rounded px-2 py-1 text-xs"
                  />
                </div>
                <button
                  onClick={load}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                  data-testid="apply-filters-btn"
                >
                  Apply
                </button>
              </div>

              <table className="w-full text-xs" data-testid="audit-table">
                <thead>
                  <tr className="border-b text-left text-gray-600 uppercase">
                    <th className="px-2 py-2">When</th>
                    <th className="px-2 py-2">User</th>
                    <th className="px-2 py-2">Action</th>
                    <th className="px-2 py-2">Resource</th>
                    <th className="px-2 py-2">Duration</th>
                    <th className="px-2 py-2">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(l => (
                    <tr key={l.id} className="border-b hover:bg-gray-50" data-testid={`log-row-${l.id}`}>
                      <td className="px-2 py-1.5 whitespace-nowrap text-gray-600">{new Date(l.timestamp).toLocaleString()}</td>
                      <td className="px-2 py-1.5 text-gray-700">{l.actor_email || l.actor_name || l.actor_id || '--'}</td>
                      <td className="px-2 py-1.5">
                        <span className={`px-2 py-0.5 rounded ${ACTION_COLORS[l.action] || 'bg-gray-100 text-gray-600'}`}>
                          {ACTION_LABELS[l.action] || l.action}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-gray-700 truncate max-w-[240px]">{l.resource_name || l.resource_id || '--'}</td>
                      <td className="px-2 py-1.5 text-gray-500">{l.duration_seconds ? formatDuration(l.duration_seconds) : '--'}</td>
                      <td className="px-2 py-1.5 text-gray-400 font-mono">{l.ip_address || '--'}</td>
                    </tr>
                  ))}
                  {logs.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-8 text-gray-500">No activity yet</td></tr>
                  )}
                </tbody>
              </table>
            </>
          ) : tab === 'leaderboard' ? (
            <div>
              <p className="text-xs text-gray-500 mb-3">Top users ranked by total time spent viewing documents.</p>
              {(!summary || summary.leaderboard.length === 0) ? (
                <div className="py-8 text-center text-gray-500">No activity yet</div>
              ) : (
                <table className="w-full text-sm" data-testid="leaderboard-table">
                  <thead>
                    <tr className="border-b text-left text-xs text-gray-600 uppercase">
                      <th className="px-3 py-2 w-10">#</th>
                      <th className="px-3 py-2">User</th>
                      <th className="px-3 py-2 text-right">Total Time</th>
                      <th className="px-3 py-2 text-right">Views</th>
                      <th className="px-3 py-2 text-right">Downloads</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.leaderboard.map((u, i) => (
                      <tr key={u.user_id} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-500 font-mono">{i + 1}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            {i === 0 && <Flame size={13} className="text-orange-500" />}
                            <span>{u.user_email}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right text-gray-900 font-medium">
                          <Clock size={11} className="inline mr-1 text-gray-400" />
                          {formatDuration(u.total_seconds)}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-600">{u.views}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{u.downloads}</td>
                        <td className="px-3 py-2 text-right text-gray-500">{u.actions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <Heatmap summary={summary} />
          )}
        </div>
      </div>
    </div>
  );
}

function Heatmap({ summary }) {
  if (!summary) return <div className="py-8 text-center text-gray-500">Loading...</div>;
  const { files = [], heatmap = [], leaderboard = [] } = summary;
  if (files.length === 0 || leaderboard.length === 0) {
    return (
      <div className="py-8 text-center text-gray-500">
        No engagement data yet. The heatmap will appear once users start viewing documents.
      </div>
    );
  }

  // Build lookup
  const key = (u, f) => `${u}|${f}`;
  const map = {};
  heatmap.forEach(h => { map[key(h.user_id, h.file_id)] = h; });

  // Compute max seconds for color scale
  const maxSec = Math.max(1, ...heatmap.map(h => h.seconds || 0));

  const colorFor = (sec) => {
    if (!sec) return 'bg-gray-50';
    const intensity = Math.min(1, sec / maxSec);
    // 5 buckets of blue
    if (intensity > 0.8) return 'bg-blue-600 text-white';
    if (intensity > 0.6) return 'bg-blue-500 text-white';
    if (intensity > 0.4) return 'bg-blue-400 text-white';
    if (intensity > 0.2) return 'bg-blue-200 text-blue-900';
    return 'bg-blue-100 text-blue-800';
  };

  return (
    <div>
      <p className="text-xs text-gray-500 mb-3">Users × Files — cell intensity scales with time spent.</p>
      <div className="overflow-auto border rounded">
        <table className="text-[11px]" data-testid="heatmap-table">
          <thead>
            <tr>
              <th className="sticky left-0 bg-white border-r border-b px-2 py-2 text-left font-medium text-gray-700 min-w-[200px] z-10">User</th>
              {files.map(f => (
                <th key={f.id} className="border-b px-2 py-2 text-left font-medium text-gray-700 min-w-[120px] max-w-[160px]">
                  <div className="truncate" title={f.name}>
                    {f.is_high_value && <Flame size={10} className="inline text-orange-500 mr-0.5" />}
                    {f.name}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leaderboard.map(u => (
              <tr key={u.user_id}>
                <td className="sticky left-0 bg-white border-r border-b px-2 py-2 font-medium text-gray-700">{u.user_email}</td>
                {files.map(f => {
                  const cell = map[key(u.user_id, f.id)] || { seconds: 0, views: 0 };
                  return (
                    <td
                      key={f.id}
                      className={`border-b px-2 py-2 text-center ${colorFor(cell.seconds)}`}
                      title={`${cell.views || 0} views • ${formatDuration(cell.seconds)}`}
                    >
                      {cell.seconds ? formatDuration(cell.seconds) : '—'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
